import React, { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTheme } from '../../../context/ThemeContext';
import { dropdownItemStyle } from './CustomDropdownStyles';

/**
 * ------------------------------------------------------------------
 * CustomDropdown
 * Componente de dropdown personalizado con soporte para avatar e iniciales
 * ------------------------------------------------------------------
 *
 * PROPS:
 * @param {string} label - Texto del botón dropdown
 * @param {string} icon - Clase de icono Bootstrap (ej: 'bi-gear')
 * @param {string} avatar - URL de la imagen del avatar
 * @param {string} initials - Iniciales a mostrar cuando no hay avatar (máx 2 letras)
 * @param {Array} items - Array de items del menú dropdown
 *   - item.label: Texto del item
 *   - item.icon: Icono del item
 *   - item.onClick: Función al hacer click
 *   - item.divider: true para mostrar un divisor
 *   - item.isActive: true para marcar como activo
 * @param {string} align - Alineación del menú ('start' | 'end')
 * @param {object} toggleProps - Props adicionales para el Toggle
 * @param {object} menuProps - Props adicionales para el Menu
 */
function CustomDropdown({ label, icon, avatar, initials, items = [], align = "end", toggleProps = {}, menuProps = {}, show, onToggle }) {
  const { dark } = useTheme();
  const [hoveredIdx, setHoveredIdx] = useState(null);

  // Evitar pasar atributos booleanos no válidos al DOM (p. ej. `show`)
  const { show: _toggleShow, ...safeToggleProps } = toggleProps || {};
  const { show: _menuShow, ...safeMenuProps } = menuProps || {};

  return (
    <Dropdown align={align} strategy="fixed" show={show} onToggle={onToggle}>
      <Dropdown.Toggle
        variant={dark ? "secondary" : "light"}
        id="custom-dropdown-toggle"
        {...safeToggleProps}
        className={`custom-dropdown-toggle ${dark ? 'custom-dropdown-toggle-dark' : 'custom-dropdown-toggle-light'} border-0 d-inline-flex align-items-center gap-2 btn-uniform-height ${safeToggleProps.className || ''}`}
        style={safeToggleProps.style}
      >
        {avatar ? (
          <img
            src={avatar}
            alt="Profile"
            className={`custom-dropdown-avatar ${dark ? 'custom-dropdown-avatar-dark' : 'custom-dropdown-avatar-light'}`}
          />
        ) : initials ? (
          <div className={`custom-dropdown-initials ${dark ? 'custom-dropdown-initials-dark' : 'custom-dropdown-initials-light'}`}>
            {initials}
          </div>
        ) : null}
        {icon && <i className={`bi ${icon}`} style={{ border: 'none' }}></i>}
        {label}
        {/* Chevron indicator for dropdown */}
        {(avatar || initials) && (
          <i className="bi bi-chevron-down ms-1" style={{ fontSize: '12px', opacity: 0.7 }}></i>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu
        {...safeMenuProps}
        className={`custom-dropdown-menu ${dark ? 'custom-dropdown-menu-dark' : 'custom-dropdown-menu-light'} ${safeMenuProps.className || ''}`}
        style={safeMenuProps.style}
      >
        {items.map((item, idx) =>
          item.divider ? (
            <Dropdown.Divider key={idx} />
          ) : (
            <Dropdown.Item
              key={idx}
              as={item.as || "button"}
              href={item.href}
              onClick={item.onClick}
              className={item.className}
              style={{
                ...dropdownItemStyle(
                  dark,
                  !!item.isActive,
                  hoveredIdx === idx
                ),
                ...item.style,
              }}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {item.icon && <i className={`bi ${item.icon} me-2`}></i>}
              {item.label}
            </Dropdown.Item>
          )
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default CustomDropdown;